import { useEffect, useState } from 'react';

/** How long the improved prompt takes to wipe in. The CSS reads it through
 *  `--improve-reveal-ms`, so the animation and this timer cannot drift. */
export const IMPROVE_REVEAL_MS = 650;

/**
 * True for `IMPROVE_REVEAL_MS` after an improve finishes with new text: the
 * window in which the composer wipes the improved prompt in. An improve that
 * fails or returns the same text plays nothing.
 *
 * Derived during render, not in an effect: the first frame that shows the
 * improved text already has the reveal on, so the text never flashes in full
 * and the dust stays mounted to make its last pass.
 */
export function useImproveReveal(isImproving: boolean, value: string): boolean {
  const [wasImproving, setWasImproving] = useState(isImproving);
  const [valueAtStart, setValueAtStart] = useState(value);
  const [revealing, setRevealing] = useState(false);

  if (isImproving !== wasImproving) {
    setWasImproving(isImproving);
    if (isImproving) {
      setValueAtStart(value);
      setRevealing(false);
    } else if (value !== valueAtStart) {
      setRevealing(true);
    }
  }

  // Keyed on `revealing` alone, so typing during the reveal neither restarts
  // nor cancels it.
  useEffect(() => {
    if (!revealing) return undefined;
    const timer = window.setTimeout(
      () => setRevealing(false),
      IMPROVE_REVEAL_MS
    );
    return () => window.clearTimeout(timer);
  }, [revealing]);

  return revealing;
}
